import React, { useState, useEffect } from 'react';
import i18next from 'i18next';
import { Row, Col, Button, Modal } from 'react-bootstrap';
import ReactPanZoom from "@ajainarayanan/react-pan-zoom";
import ScrollArea from 'react-scrollbar';

import { Message } from '../Message';
import { PageTitle } from '../PageTitle';
import { AssetsService, DeviceDataOverrideService, EntityService } from '../../services';
import { ContentService } from '../../services';
import { OpSenseToastContainer, toast } from '../Toast';
import AssetImageGallery from './asset_image_gallery';
import { DeviceDataGraphModal } from "../Common";
import { TimeFormattingService } from '../../services';

import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import faMapPin from '@fortawesome/fontawesome-free-solid/faMapPin';

import config from "config";
import { FormatHelper } from '../../helpers';

const AssetView = (props) => {
  const [assetId] = useState(props.match.params.assetId);
  const [backLinkSearch] = useState(props.location && props.location.state && props.location.state.backLinkSearch ? props.location.state.backLinkSearch : null);
  const [searchValue] = useState(
    props.location && props.location.state && props.location.state.searchValue
      ? props.location.state.searchValue
      : null
  );
  const [asset, setAsset] = useState(null);
  const [floorPlanURL, setFloorPlanURL] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showFloorplanModal, setShowFloorplanModal] = useState(false);
  const [tags, setTags] = useState([]);
  const [area, setArea] = useState(null);
  const [assetImages, setAssetImages] = useState([]);
  const [eventConfigurations, setEventConfigurations] = useState([]);
  const [exceededThresholds, setExceededThresholds] = useState([]);
  const toggleFloorplanModal = () => setShowFloorplanModal(!showFloorplanModal);

  const refresh = () => {
    setRefreshing(true);

    AssetsService.get(assetId).then((response) => {

      EntityService.getEventConfigurations(response.asset.serialNumber).then(({ configurations }) => {          
          setEventConfigurations(configurations);
      });

      EntityService.getExceededThresholds(response.asset.serialNumber).then((response) => {
          setExceededThresholds(response.exceededThresholds);
      });

      ContentService.getDeviceGroupingFloorplanImage(response.asset.businessGroup.id).then(floorPlanURL => {
        const imageFetches = response.asset.assetImages.map(async image => ({id:image.id, src:await ContentService.getAssetImage(response.asset.id, image.name), primary:image.primary && image.primary, original:true, deleted:false, replaced:false}));
        Promise.all(imageFetches).then(fetchedAssetImages => {
          setTags(response.asset.tags.filter(tag => tag.tagType.token != 'area'));
          setArea(response.asset.tags.find(tag => tag.tagType.token == 'area'));
          setFloorPlanURL(floorPlanURL);
          setAssetImages(fetchedAssetImages);
          setAsset(response.asset);
        });      
      });
      setRefreshing(false);
    })
    .catch((error) => {
      console.error(error);
      toast.error(i18next.t('messages.could-not-load-asset-view'));
      setRefreshing(false);
    });
  };
  
  useEffect(() => {
    refresh();
  }, [assetId]);

  return asset && floorPlanURL && !refreshing ? (
      <div className="assets">

      <OpSenseToastContainer />

      <AssetFloorplanModal show={showFloorplanModal} toggleModal={toggleFloorplanModal} location={{x:asset.floorPlanX, y:asset.floorPlanY}} floorPlanURL={floorPlanURL} />

      <div className="row">
        <PageTitle 
        title={i18next.t('pages.assets.view-asset')} 
        backLinkWithParams={{ link: { pathname: "/config/assets", search: backLinkSearch, state: { searchValue: searchValue } } }}
        refreshFunction={refresh}
          editLinkWithParams={{
            link: {
              pathname: `/config/assets/edit/${assetId}`,
              uiroute: "/config/assets/edit/:assetId",
              state: {
                backLinkSearch: backLinkSearch,
                searchValue: searchValue,
              },
            },
          }}
          />
      </div>

      <div className="op-content-box">
        <Message messages={props.location} />

        <Row>
          <Col xs={12} sm={4}>
          <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-name">
              <label>{i18next.t("pages.assets.name")}</label>
              <p>{asset.name}</p>
            </div>
          </Col>
          <Col xs={12} sm={4}>
          <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-group">
              <label>{i18next.t("pages.assets.business-group")}</label>
              <p>{asset.businessGroup.name}</p>
            </div>
          </Col>
          <Col xs={12} sm={4}>
          <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-type">
              <label>{i18next.t("pages.assets.type")}</label>
              <p>{asset.assetType.name}</p>
            </div>
          </Col>
        </Row>

        <br/>

        <Row>
          <Col xs={12} sm={4}>
            <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-info-generic">
                <label>{i18next.t("pages.assets.tags")}</label>
                <p>{tags.map((tag) => tag.name)}</p>
              </div>
          </Col>
          <Col xs={12} sm={4}>
          <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-location">
              <label>{i18next.t("pages.assets.area")}</label>
              <p>{area && area.name}</p>
            </div>
          </Col>        
          <Col xs={12} sm={4}>
            {asset.assetType.requiresFloorplan && <div>
              <label>&nbsp;</label>
              { asset.floorPlanX == -1 ? 
                <span>{i18next.t('pages.assets.floorplan-location-not-set')}</span> :
                <button className="btn btn-default btn-sm" type="button" onClick={toggleFloorplanModal}>{i18next.t('pages.assets.floor-plan')}</button>
              }
            </div>}
          </Col>
        </Row>

        <br/>

        <Row>
          <Col xs={12} sm={12}>
          <div className="panel panel-default">
              <div className="panel-heading">
                {i18next.t("pages.assets.measures")}
              </div>
              <AssetMeasuresTable assetId={asset.id} measures={asset.assetMeasures} assetTypeMeasures={asset.assetType.measures} />
            </div>
          </Col>
        </Row>

        <br/>

        <Row>
          <Col xs={12} sm={6}>
          <div className="panel panel-default">
              <div className="panel-heading">
                {i18next.t("pages.assets.metadata")}
              </div>
              <AssetMetadataTable metadata={asset.assetMetadata} />
            </div>
          </Col>
          <Col xs={12} sm={6}>
          <div className="panel panel-default">
              <div className="panel-heading">
                {i18next.t("pages.assets.asset-images")}
              </div>
                <AssetImageGallery assetImages={assetImages} onError={(err) => { toast.error(err); }} viewOnly={true} />
            </div>
          </Col>
        </Row>   
        
        <Row>
            <Col xs={12} sm={12}>
                <div className="panel panel-default device-table op-panel">
                    <div className="panel-heading">{i18next.t("pages.device-configuration.applicable-events-and-thresholds")}</div>
                    {eventConfigurations.length === 0 ? (
                        <div style={{ paddingBottom: 5 }}>{i18next.t("messages.no-events-or-thresholds")}</div>
                    ) : (
                        eventConfigurations.map((config) => (
                            <AlertConfigRow
                                key={config.id}
                                config={config}
                                measures={asset.assetMeasures}
                                thresholdsExceeded={exceededThresholds}
                            />
                        ))
                    )}
                </div>
            </Col>
        </Row>
      </div>
    </div>) : (<div className="loader">{i18next.t('loading')}</div>);
}

const AlertConfigRow = ({config, measures, thresholdsExceeded}) => {
  const [linkTo, setLinkTo] = useState(null);
  const unique = a => [...new Set(a.map(o => JSON.stringify(o)))].map(s => JSON.parse(s))
  const associatedThresholds = unique(config.alertConfigurationActions.map((action) => action.threshold));

  if (linkTo != null) {
    return <Redirect to={{ pathname: linkTo }} />;
  }

  return (
    <div key={config.id} className="device-table" style={{paddingTop: '5px'}}>
      <table className="table table-striped">
        <thead>
          <tr key={config.id}>
            <th onClick = {() => { setLinkTo(`/config/events/view/${config.id}`); }}>Alert: {config.name}</th>
          </tr>
        </thead>
        <tbody key={config.id}>
          {
            associatedThresholds.length === 0 ? 
              <tr key={1}><td>{ i18next.t('messages.no-thresholds') }</td></tr> : 
              associatedThresholds.map(threshold => <DeviceThresholdRow key={threshold.id} threshold={threshold} measures={measures} thresholdsExceeded={thresholdsExceeded} /> )
          }
        </tbody>
      </table>
    </div>
  );
};

const DeviceThresholdRow = ({threshold, measures, thresholdsExceeded}) => {
  const measurementValue = FormatHelper.formatMeasurement(threshold);
  return measures
    .map((measure) => {
      if (measure.assetTypeDeviceDataType?.deviceDataClassification?.token && threshold.deviceDataClassification?.token &&
        measure.assetTypeDeviceDataType?.deviceDataClassification?.token === threshold.deviceDataClassification?.token) {

        let dataTypeToken = measure.deviceDataType.token;
        if (thresholdsExceeded) {
          const foundThresholdExceeded = thresholdsExceeded.filter((thresholdExceeded) => { 
            if (thresholdExceeded.deviceDataType?.deviceDataClassification) {
              return thresholdExceeded.deviceDataType.deviceDataClassification.id === measure.deviceDataType.deviceDataClassification.id;
            } else {
              return false;
            }
          });
          if (foundThresholdExceeded && foundThresholdExceeded.length > 0) {
            dataTypeToken = foundThresholdExceeded[0].thresholdClassification.token;
          }
        }

        const fnToApply = DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(measure.deviceDataType.token);
        let lastReceivedData = { ...measure.lastReceivedData, numericValue: fnToApply(measure.lastReceivedData?.numericValue) };

        return (
          <tr key={`${threshold.id}-${measure.deviceDataType.id}`}>
            <td>
              { i18next.t('pages.device-configuration.threshold') } {threshold.name}&nbsp;
              <span>              
                <img src={`${config.WEB_BASE_URL}/images/icon/sensor_${dataTypeToken}.svg`} height="16" /> Current evaluation of {measure.deviceDataType.name}: {FormatHelper.formatMeasurement(threshold, lastReceivedData)}&nbsp;
                <span className="threshold-evaluation">{threshold.thresholdEvaluationType.shortForm}</span>&nbsp;
                {measurementValue}
              </span>
            </td>        
          </tr>
        );
      } else {
        return null;
      }
    })
    .filter(entry => entry); // Remove null entries
}

const AssetFloorplanModal = ({show, toggleModal, location, floorPlanURL}) => {
  const [zoom, setZoom] = useState(1);

  return (
      <Modal show={show} onHide={toggleModal} dialogClassName="op-floor-plan-modal">
          <Modal.Header closeButton>
            <Modal.Title>{i18next.t('pages.assets.asset-location')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ScrollArea speed={0.8} className="scroll-container" contentClassName="scrollable-content" horizontal={true} >
              <ReactPanZoom zoom={zoom}>
                <div className="store-map-image-wrapper" style={{position: 'relative', backgroundImage: `url(${floorPlanURL})`, width:700, height:500, backgroundSize: '700px 500px'}}>
                  <FontAwesomeIcon icon={faMapPin} size={'lg'} style={{ position: 'absolute', top: location.y-20, left: location.x-6, color: '#44f' }} />
                </div>    
              </ReactPanZoom>

              <div className="op-floor-plan-zoom">
                <div className="row">
                  <div className="col-sm-12">
                    <button onClick={() => {setZoom(zoom + 0.2)}} type="button" className="btn btn-default btn-block btn-following-row"><span>+</span></button>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12">
                    <button onClick={() => {if (zoom > 0.2) setZoom(zoom - 0.2)}} type="button" className="btn btn-default btn-block btn-following-row"><span style={{fontSize: '18px', fontWeight: '600', lineHeight:'22px'}}>-</span></button>
                  </div>
                </div>
              </div>                      
            </ScrollArea>             
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={toggleModal}>{i18next.t('form-labels.cancel')}</Button>
          </Modal.Footer>
      </Modal>
  );
}

const AssetMeasuresTable = ({assetId, measures, assetTypeMeasures}) => {
  const [showDeviceGraphModal, setShowDeviceGraphModal] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState(null);

  const mergeMeasures = () => {
    const merged = assetTypeMeasures.map(assetTypeMeasure => {
      const foundMeasure = measures.find(measure => measure.assetTypeDeviceDataType.id == assetTypeMeasure.id);
      if (foundMeasure != null) {
        if (foundMeasure.device && foundMeasure.device.deviceLastReceivedData) {
          foundMeasure.lastReceivedData = foundMeasure.device.deviceLastReceivedData.find((lrd) => lrd.deviceDataType.id == foundMeasure.deviceDataType.id);
        }
        return foundMeasure;
      } else {
        return {id:'atm-' + assetTypeMeasure.id, device:null, deviceDataType:null, startAt:null, endAt:null, assetTypeDeviceDataType:assetTypeMeasure};
      }
    });
    merged.sort((a, b) => (a.assetTypeDeviceDataType.displayOrder === b.assetTypeDeviceDataType.displayOrder) ? 0 : ((a.assetTypeDeviceDataType.displayOrder < b.assetTypeDeviceDataType.displayOrder) ? -1 : 1));
    return merged;
  };

  return (
    <table className='table table-striped'>
      <thead>
        <tr>
          <th>{i18next.t('pages.assets.name')}</th>
          <th>{i18next.t('pages.assets.measure-type')}</th>
          <th>{i18next.t('pages.assets.measured-from')}</th>
          <th>{i18next.t('form-labels.last-updated')}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {
          mergeMeasures().map((measure) => (
            <tr key={`${measure.id}`}>
              <td>{measure.assetTypeDeviceDataType.name}</td>
              <td>{measure.assetTypeDeviceDataType.deviceDataClassification.name}</td>
              <td>
                {
                  measure.device && measure.deviceDataType ? `${measure.device.displayName} : ${measure.deviceDataType.name}` : i18next.t('pages.assets.not-set')
                }
              </td>
              <td>{measure.lastReceivedData && TimeFormattingService.renderFromNowTime(measure.lastReceivedData.deviceTime, measure.device.deviceGrouping ? measure.device.deviceGrouping.timezone : null)}</td>
              <td>
              {
                measure.device ?
                  <a className="btn btn-sm btn-default" onClick={() => { setSelectedMeasure(measure); setShowDeviceGraphModal(!showDeviceGraphModal); }} >
                      <img className="small-inline-icon" src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/charts.svg`} alt={i18next.t("data-chart")} />
                      {i18next.t("data-chart")}
                  </a>
                 : null
              }
              </td>
            </tr>
          ))
        }
      </tbody>
      {
        selectedMeasure ? <DeviceDataGraphModal 
            entityTypeToken='asset'
            entityId={assetId} 
            entityName={selectedMeasure.device.displayName}
            entity={selectedMeasure.device}
            singleSelectedMeasurement={selectedMeasure}
            dataType={selectedMeasure.deviceDataType}
            timezone={selectedMeasure.device.deviceGrouping ? selectedMeasure.device.deviceGrouping.timezone : null}
            show={showDeviceGraphModal}
            onHide={() => { setShowDeviceGraphModal(!showDeviceGraphModal); }}
        /> : null
      }
    </table>
  );
};

const AssetMetadataTable = ({metadata}) => {
  return (
    <table className='table table-striped'>
      <thead>
        <tr><th>{i18next.t('pages.assets.name')}</th><th>{i18next.t('pages.assets.value')}</th></tr>
      </thead>
      <tbody>
        {
          metadata.map((metadataEntry) => (
            <tr key={metadataEntry.id}>
              <td>{metadataEntry.assetTypeMetadataType.name}</td>
              <td>{metadataEntry.stringValue ? metadataEntry.stringValue : (metadataEntry.numericValue ? metadataEntry.numericValue : '')}</td>
            </tr>
          ))
        }
      </tbody>
    </table>
  );
};

export default AssetView;
